import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * SplitReveal — premium masked text reveal.
 *
 * Splits its text into lines (measured from the REAL wrapped layout, so it
 * respects the element's own font/width/max-width), wraps each line in an
 * overflow:hidden mask, and slides the lines up from behind that mask with a
 * stagger. This is the "editorial" reveal — far more refined than opacity
 * fades, and it is the site's standard treatment for major headings.
 *
 *   <SplitReveal as="h2" className="hv-title">Four locations. One standard.</SplitReveal>
 *
 * Props:
 *   as        — element tag to render (default "div")
 *   stagger   — seconds between line entrances
 *   delay     — seconds before the first line
 *   duration  — seconds per line
 *   y         — how far each line travels (px, from below the mask)
 *   blur      — add a subtle blur-to-sharp on entry
 *   start     — ScrollTrigger start (default "top 85%")
 *   once      — play once (default true); false re-plays on re-entry
 *
 * Accessibility / safety:
 *   - Under prefers-reduced-motion the text renders plainly, no splitting.
 *   - The original text stays in the DOM as the accessible label; the split
 *     lines are aria-hidden, so screen readers never hear fragmented text.
 *   - Re-splits on resize (debounced) so line breaks always match the layout.
 */
export default function SplitReveal({
  as: Tag = "div",
  children,
  className = "",
  stagger = 0.09,
  delay = 0,
  duration = 0.9,
  y = 100,
  blur = false,
  start = "top 85%",
  once = true,
  ...rest
}) {
  const ref = useRef(null);

  // Only plain text can be split safely. If a caller passes elements (e.g. an
  // <em> accent), we render them untouched rather than flattening the markup.
  const text = typeof children === "string" ? children : null;

  useEffect(() => {
    const el = ref.current;
    if (!el || text === null) return undefined;

    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    // Source of truth is the PROP, not the DOM — so React re-renders and our
    // splitting can never drift out of sync.
    const original = text;
    let ctx;
    let resizeTimer;
    let failSafe;

    const split = () => {
      // Rebuild from the original text every time.
      el.textContent = original;

      // Wrap each WORD so we can detect line boundaries by offsetTop.
      //
      // The spans stay `inline` (NOT inline-block): inline-block changes how
      // the browser breaks lines, so the measured wrap points would not match
      // the wrap points the user actually sees. Real whitespace text nodes are
      // preserved between words for the same reason.
      const words = original.split(/(\s+)/);
      el.textContent = "";
      const wordSpans = [];
      words.forEach((w) => {
        if (/^\s+$/.test(w)) {
          el.appendChild(document.createTextNode(w));
          return;
        }
        const s = document.createElement("span");
        s.textContent = w;
        el.appendChild(s);
        wordSpans.push(s);
      });

      // Group words into lines by their vertical position. getBoundingClientRect
      // is used rather than offsetTop because line boxes are frequently at
      // fractional offsets, which integer offsetTop rounds inconsistently.
      // A word starts a new line when its top jumps by more than half a line.
      const lines = [];
      let lineTop = null;
      let bucket = [];
      wordSpans.forEach((s) => {
        const top = s.getBoundingClientRect().top;
        if (lineTop === null) {
          lineTop = top;
          bucket.push(s);
        } else if (Math.abs(top - lineTop) <= 3) {
          bucket.push(s);
        } else {
          lines.push(bucket);
          bucket = [s];
          lineTop = top;
        }
      });
      if (bucket.length) lines.push(bucket);

      // Preserve the heading's own alignment: stacking lines as block rows
      // otherwise silently left-aligns centred headings.
      const align = window.getComputedStyle(el).textAlign;

      // Rebuild the element as masked line rows.
      el.textContent = "";
      const inners = [];
      lines.forEach((lineWords) => {
        const mask = document.createElement("span");
        mask.className = "split-line";
        mask.style.textAlign = align;
        const inner = document.createElement("span");
        inner.className = "split-line__inner";
        inner.textContent = lineWords.map((w) => w.textContent).join(" ");
        mask.appendChild(inner);
        el.appendChild(mask);
        inners.push(inner);
      });

      // The visible split text is decorative; the accessible name is set
      // below via aria-label so SRs read one clean string.
      el.setAttribute("aria-label", original);

      // No lines measured (hidden container, zero width, fonts not ready) —
      // bail out and leave the plain text visible rather than animating
      // nothing into a permanently-hidden state.
      if (!inners.length) {
        el.textContent = original;
        return;
      }

      ctx = gsap.context(() => {
        const tween = gsap.fromTo(
          inners,
          { yPercent: 115, ...(blur ? { filter: "blur(8px)" } : {}) },
          {
            yPercent: 0,
            ...(blur ? { filter: "blur(0px)" } : {}),
            duration,
            delay,
            stagger,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start, once },
          }
        );

        // FAIL-SAFE: headings must never stay stuck behind their mask. If the
        // ScrollTrigger hasn't fired within 2.5s (stale bounds, trigger above
        // the fold, refresh race), force the text visible. Text being readable
        // always outranks the reveal playing.
        failSafe = setTimeout(() => {
          const st = tween.scrollTrigger;
          if (!st || st.progress === 0) {
            gsap.set(inners, { yPercent: 0, filter: "none", clearProps: "filter" });
          }
        }, 2500);
      }, el);
    };

    // Fonts change wrapping — split after they settle to avoid wrong lines.
    const run = () => split();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run).catch(run);
    } else {
      run();
    }

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        clearTimeout(failSafe);
        if (ctx) ctx.revert();
        split();
        ScrollTrigger.refresh();
      }, 220);
    };
    window.addEventListener("resize", onResize);

    return () => {
      clearTimeout(resizeTimer);
      clearTimeout(failSafe);
      window.removeEventListener("resize", onResize);
      if (ctx) ctx.revert();
      // Restore the clean text so React's DOM matches what it expects.
      if (el) {
        el.textContent = original;
        el.removeAttribute("aria-label");
      }
    };
    // eslint-disable-next-line
  }, [text, stagger, delay, duration, y, blur, start, once]);

  return (
    // suppressHydrationWarning: after mount we replace the text with masked
    // line spans, so the DOM intentionally differs from React's render.
    <Tag
      ref={ref}
      className={`split-reveal ${className}`}
      suppressHydrationWarning
      {...rest}
    >
      {children}
    </Tag>
  );
}
